import { useRef, useState, type ChangeEvent } from 'react';
import { cx } from '../../lib/cx';

export interface OtpInputProps {
  /** Number of digits (default 6). */
  length?: number;
  value: string;
  onChange: (value: string) => void;
  /** Fires once, when the last digit is entered or a full code is pasted. */
  onComplete?: (code: string) => void;
  disabled?: boolean;
  error?: boolean;
  autoFocus?: boolean;
  'aria-label'?: string;
  className?: string;
}

/** Strips everything but digits, so "123 456", "123-456" and pasted codes all work. */
export const sanitizeOtp = (raw: string, length: number): string => raw.replace(/\D/g, '').slice(0, length);

/**
 * Figma "C01.9 · Verify email" code cells: 58x60, radius 16, 1px outline, 2px brand outline on the active
 * cell (red when wrong). One real (transparent) input sits over the cells, so typing, paste, backspace, iOS/Android SMS
 * autofill (`one-time-code`) and screen readers all use the platform behaviour instead of faked key handling.
 */
export function OtpInput({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled = false,
  error = false,
  autoFocus = false,
  'aria-label': ariaLabel = 'Verification code',
  className,
}: OtpInputProps) {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = sanitizeOtp(e.target.value, length);
    onChange(next);
    if (next.length === length && value.length !== length) onComplete?.(next);
  };

  const activeIndex = Math.min(value.length, length - 1);

  return (
    <div
      className={cx(
        'relative flex justify-center gap-(--mbm-space-2-5)',
        disabled && 'opacity-(--mbm-opacity-disabled)',
        className,
      )}
    >
      <input
        ref={inputRef}
        value={value}
        onChange={handleChange}
        onFocus={(e) => {
          setFocused(true);
          // Typing only ever appends, so keep the caret at the end even if the user clicks mid-string.
          const end = e.target.value.length;
          e.target.setSelectionRange(end, end);
        }}
        onBlur={() => setFocused(false)}
        disabled={disabled}
        autoFocus={autoFocus}
        inputMode="numeric"
        autoComplete="one-time-code"
        aria-label={ariaLabel}
        aria-invalid={error || undefined}
        className="absolute inset-0 z-10 size-full cursor-text opacity-0 caret-transparent"
      />
      {Array.from({ length }, (_, i) => {
        const digit = value[i] ?? '';
        const isActive = focused && i === activeIndex && !disabled;
        return (
          <div
            key={i}
            aria-hidden
            className={cx(
              'flex h-(--mbm-size-otp-cell-wide-height) w-(--mbm-size-otp-cell-wide-width) items-center justify-center',
              'rounded-(--mbm-radius-md) border text-heading-h1 text-text-strong',
              // Figma "Verify · Wrong code": cells turn error-tinted with a red stroke; digits stay dark.
              error ? 'border-feedback-error bg-feedback-error-bg' : 'border-border-cell bg-surface-card',
              isActive && 'outline-2 -outline-offset-2',
              isActive && (error ? 'outline-feedback-error' : 'outline-brand-primary'),
            )}
          >
            {digit}
          </div>
        );
      })}
    </div>
  );
}
